import { useTranslation } from 'react-i18next';

/**
 * "Photo by {photographer} on {Source}" caption for free stock photos.
 * Unsplash's API terms REQUIRE this credit (with links) wherever one of
 * their photos is displayed; Pexels/Pixabay appreciate it. Renders nothing
 * when there is no stock attribution (AI-generated or hand-uploaded images).
 */
export interface StockAttribution {
  source: string;
  photographer: string | null;
  photographer_url: string | null;
  page_url: string | null;
}

const SOURCE_NAMES: Record<string, string> = {
  unsplash: 'Unsplash',
  pexels: 'Pexels',
  pixabay: 'Pixabay',
};

/** Unsplash attribution links must carry these UTM params per their guidelines. */
function withUtm(url: string | null, source: string): string | null {
  if (!url) return null;
  if (source !== 'unsplash') return url;
  return `${url}${url.includes('?') ? '&' : '?'}utm_source=rumio&utm_medium=referral`;
}

function CreditLink({ href, label }: { href: string | null; label: string }) {
  if (!href) return <span>{label}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="underline decoration-dotted underline-offset-2 hover:text-ink"
    >
      {label}
    </a>
  );
}

export default function StockPhotoCredit({
  attribution,
  className = 'mt-2 text-right text-xs text-ink-muted',
}: {
  attribution: StockAttribution | null | undefined;
  className?: string;
}) {
  const { t } = useTranslation('common');
  if (!attribution?.photographer) return null;

  return (
    <figcaption className={className}>
      {t('stockPhotoCreditBy')}{' '}
      <CreditLink
        href={withUtm(attribution.photographer_url, attribution.source)}
        label={attribution.photographer}
      />{' '}
      {t('stockPhotoCreditOn')}{' '}
      <CreditLink
        href={withUtm(attribution.page_url, attribution.source)}
        label={SOURCE_NAMES[attribution.source] ?? attribution.source}
      />
    </figcaption>
  );
}
